import * as React from "react";
import {FC, useEffect, useState} from "react";
import ReactDOM from "react-dom";
import {useFormContext} from "react-hook-form";
import {Dialog, DialogContent, IconButton, Menu, MenuItem, Paper, Tooltip} from "@mui/material";
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import Avatar from "@mui/material/Avatar";
import {getActivationColor} from './ActivationColors';

import {
   Brick,
   BrickBox,
   BrickButton,
   BrickImage,
   BrickMailDefinition,
   BrickMailInstance,
   BrickProducts,
   BrickShipping,
   BrickStatus,
   BrickText
} from "../BrickMailType";
import {BRICK_TYPES, createNewBrick} from '../BrickTypeDefinition';
import {
   BoxBrickHtml,
   ButtonBrickHtml,
   ImageBrickHtml,
   ProductsBrickHtml,
   ShippingBrickHtml,
   StartHtml,
   StatusBrickHtml,
   TextBrickHtml
} from "../SendGridData";
import {useClipboard} from "../Clipboard";
import {StringHelper} from "../../../../utility/StringHelper";
import {BodyText, TitleText} from "../../../../layout/Typography";
import {useMailEditorStyles} from "../styles/MailEditorStyles";
import {BrickEditor} from "./brickEditors/BrickEditor";
import {HeaderEditor} from "./HeaderEditor";

interface MailEditorProps {
   index: number;
}

export const MailEditor: FC<MailEditorProps> = ({index}) => {
   const [brick, setBrick] = useState<number | undefined>();
   const [header, setHeader] = useState<boolean>(false);
   const [data, setData] = useState<BrickMailDefinition | undefined>();

   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [menuBrickIndex, setMenuBrickIndex] = useState<number | null>(null);

   // Pre-process all activation expressions to ensure consistent color assignment
   const initializeActivationColors = (brickData?: BrickMailDefinition) => {
     if (brickData?.bricks) {
       // First pass to collect and assign colors to all activations
       brickData.bricks.forEach(brick => {
         if (brick.activation) {
           // Just calling this will register the color if not already assigned
           getActivationColor(brick.activation);
         }
       });
     }
   };

   const form = useFormContext<any>();
   const {setValue, getValues} = form;

   const classes = useMailEditorStyles();

   const [pasteMenuAnchorEl, setPasteMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [pastePosition, setPastePosition] = useState<number | null>(null);
   const {clipboardBrick, setClipboardBrick} = useClipboard();

   useEffect(() => {
      const json = getValues(`mails.${index}.mail`);
      const parsedData = json ? JSON.parse(json) : '';
      
      // Initialize colors for all activation expressions to ensure consistent assignment
      initializeActivationColors(parsedData);
      
      setData(parsedData);
   }, [getValues, index])

   const formSubmit = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setValue(`mails.${index}.mail`, JSON.stringify(data), {
         shouldDirty: true
      })
   }

   // Handle copying a brick
   const handleCopyBrick = (brickIndex: number) => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (!data) return;

      // Deep clone the brick to ensure we don't have reference issues
      const brickToClone = JSON.parse(JSON.stringify(data.bricks[brickIndex]));
      setClipboardBrick(brickToClone);
   };

   // Handle pasting a brick
   const handlePasteBrick = (pasteIndex: number) => {
      if (!clipboardBrick || !data) return;

      // Create a copy of the current data
      const newData = {...data};

      // Insert the copied brick at the specified position
      newData.bricks.splice(pasteIndex + 1, 0, JSON.parse(JSON.stringify(clipboardBrick)));

      // Update the data state
      setData(newData);

      // Update the form value
      setValue(`mails.${index}.mail`, JSON.stringify(newData, null, 2), {
         shouldDirty: true
      });

      // Close paste menu
      setPasteMenuAnchorEl(null);
      setPastePosition(null);
   };

   // Show paste menu
   const handleShowPasteMenu = (pasteIndex: number) => (e: React.MouseEvent<HTMLElement>) => {
      if (!clipboardBrick) return;
      e.stopPropagation();
      setPastePosition(pasteIndex);
      setPasteMenuAnchorEl(e.currentTarget);
   };

   useEffect(() => {
      const mailRoot = document.getElementById('mail-root');
      if (mailRoot && data) {
         ReactDOM.render(
            <>
               {data.bricks.map((field: any, i) => {
                  let html: string;
                  try {
                     if (field.image) {
                        html = ImageBrickHtml(field as BrickImage);
                     } else if (field.text) {
                        html = TextBrickHtml(field as BrickText, data);
                     } else if (field.button) {
                        html = ButtonBrickHtml(field as BrickButton, data);
                     } else if (field.products) {
                        html = ProductsBrickHtml(field as BrickProducts, data, mailInstance);
                     } else if (field.box) {
                        html = BoxBrickHtml(field as BrickBox, data);
                     } else if (field.shipping) {
                        html = ShippingBrickHtml(field as BrickShipping, data);
                     } else if (field.status) {
                        html = StatusBrickHtml(field as BrickStatus, data);
                     } else {
                        html = `<i>Unsupported brick type</i>`;
                     }
                  } catch (error) {
                     console.error('Error rendering brick:', error);
                     html = `<i>Error rendering brick: ${error}</i>`;
                  }

                  // Extract activation expression from the brick
                  const activationExpression = field.activation;
                  const activationColor = getActivationColor(activationExpression);
                  
                  // Determine which class to use based on whether there's an activation
                  const brickClass = activationColor ? classes.brickWithActivation : classes.brick;
                  
                  // Return the enhanced brick with control buttons
                  return (
                    <div
                      key={'brick_' + i}
                      className={brickClass}
                      onClick={onClick(i)}
                    >
                      {/* Activation indicator bar (only if brick has activation) */}
                      {activationColor && (
                        <Tooltip 
                          title={`Activation: ${activationExpression}`} 
                          placement="left"
                        >
                          <div 
                            className={classes.activationIndicator} 
                            style={{ backgroundColor: activationColor }} 
                          />
                        </Tooltip>
                      )}
                      
                      {/* Controls container with add and delete buttons */}
                      <div className={classes.brickControls}>
                        <IconButton
                          size="small"
                          className={classes.controlButton}
                          onClick={handleAddButtonClick(i)}
                        >
                          <AddIcon fontSize="small"/>
                        </IconButton>
                        <IconButton
                          size="small"
                          className={classes.controlButton}
                          onClick={handleCopyBrick(i)}
                          title="Copy brick"
                        >
                          <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                        {clipboardBrick && (
                          <IconButton
                            size="small"
                            className={classes.controlButton}
                            onClick={handleShowPasteMenu(i)}
                            title="Paste options"
                          >
                            <ContentPasteIcon fontSize="small"/>
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          className={classes.controlButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(i)();
                          }}
                        >
                          <DeleteIcon fontSize="small"/>
                        </IconButton>
                      </div>

                      {/* The brick content */}
                      <div
                        dangerouslySetInnerHTML={{__html: html}}
                      />
                    </div>
                  );
               })}
            </>,
            mailRoot
         );
      }
      // eslint-disable-next-line
   }, [data, clipboardBrick]);

   const handleAddButtonClick = (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setMenuBrickIndex(index);
      setAnchorEl(event.currentTarget);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
      setMenuBrickIndex(null);
   };

   // Refactored handleAddBrick using the brick type definitions
   const handleAddBrick = (brickType: string) => {
      if (menuBrickIndex === null || !data) return;

      // Create a new brick based on the selected type using our helper
      const newBrick = createNewBrick(brickType);

      if (!newBrick) {
         console.error(`Unknown brick type: ${brickType}`);
         return;
      }

      // Create a copy of the current data
      const newData = {...data};

      // Insert the new brick after the current brick
      newData.bricks.splice(menuBrickIndex + 1, 0, newBrick);

      // Update the data state
      setData(newData);

      // Update the form value
      setValue(`mails.${index}.mail`, JSON.stringify(newData, null, 2), {
         shouldDirty: true
      });

      // Close the menu
      handleMenuClose();
   };

   const mailInstance: BrickMailInstance = {
      products: {
         data: [{
            image: 'https://storageaccountourimbea2.blob.core.windows.net/brandstatic/App_Icon3.png',
            title: 'create2STAY - Tree',
            voucher: '100 EUR',
            voucherValue: 100,
            voucherCurrency: 'EUR',
            orderDate: '2020-02-02',
            align: 'center'
         }, {
            image: 'https://storageaccountourimbea2.blob.core.windows.net/brandstatic/App_Icon3.png',
            title: 'create2STAY - The Bag',
            voucher: '150 EUR',
            voucherValue: 150,
            voucherCurrency: 'EUR',
            orderDate: '2020-02-02',
            align: 'center'
         }],
         boughtPrefix: 'Bought',
         voucherPrefix: 'Credit'
      },
      variables: {
         customerName: 'John',
         voucher: '9999-AAAA',
         labelCode: '99LABELCODE',
         loginLink: 'https://create2stay.com/',
         registrationLink: 'https://create2stay.com/',
         shippingLink: 'https://create2stay.com/',
         shippingLinkQR: 'https://create2stay.com/qr',
         shippingImageQR: 'https://storageaccountourimbea2.blob.core.windows.net/shippinglabels/qrsample.png',
         shop: 'Shop',
         voucherValue: '100 EUR',
         voucherValueInt: '100',
         voucherLink: '#',
         locale: 'en',
         carrierName: 'Sample Carrier',
         trackingUrl: 'https://create2stay.com/tracking',
         trackingCarrier: 'Sample Carrier',
         byPassUnsubscribe: false
      }
   }

   if (data === undefined) {
      return <div>Loading...</div>
   }

   const onChange = (updated: Brick) => {
      if (brick !== undefined) {
         const newData: BrickMailDefinition = {
            ...data
         }
         newData.bricks[brick] = updated;
         
         // Initialize colors again if activation expressions have changed
         if (updated.activation) {
            getActivationColor(updated.activation);
         }
         
         setData(newData);

         setValue(`mails.${index}.mail`, JSON.stringify(newData, null, 2), {
            shouldDirty: true
         });
      }
      setBrick(undefined)
   }

   const onHeaderChange = (updated: BrickMailDefinition) => {
      if (header) {
         const bricks = data.bricks;
         const newData = {
            ...data,
            ...updated,
            bricks: bricks
         }
         setData(newData);

         setValue(`mails.${index}.mail`, JSON.stringify(newData, null, 2), {
            shouldDirty: true
         });
      }
      setHeader(false)
   }

   const onHeaderClick = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setHeader(true);
   }

   const onClick = (i: number) => (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setBrick(i);
   }

   const onDelete = (i: number) => () => {
      if (!data) return;
      const newData = {...data};
      newData.bricks.splice(i, 1);
      setData(newData);
      setValue(`mails.${index}.mail`, JSON.stringify(newData, null, 2), {
         shouldDirty: true
      });
      setBrick(undefined);
   }

   return (
      <Paper variant={"outlined"} style={{maxWidth: '602px'}}>
         {brick !== undefined &&
            <Dialog open={true} onClose={() => setBrick(undefined)} fullWidth={true} maxWidth={"md"}>
               <DialogContent>
                  <BrickEditor
                     brick={data.bricks[brick]}
                     onDelete={onDelete(brick)}
                     onChange={onChange}
                     onCancel={() => setBrick(undefined)}
                  />
               </DialogContent>
            </Dialog>
         }

         {header &&
            <Dialog open={true} onClose={() => setHeader(false)} fullWidth={true} maxWidth={"md"}>
               <DialogContent>
                  <HeaderEditor header={data} onChange={onHeaderChange} onCancel={() => setHeader(false)}/>
               </DialogContent>
            </Dialog>
         }

         <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
               vertical: 'top',
               horizontal: 'left',
            }}
            transformOrigin={{
               vertical: 'top',
               horizontal: 'left',
            }}
            // Remove disableRestoreFocus to keep the menu visible
            MenuListProps={{
               autoFocusItem: false,
            }}
            // Add style to ensure menu stays on top
            style={{zIndex: 9999}}
         >
            {BRICK_TYPES.map((brickType) => (
               <MenuItem key={brickType.type} onClick={() => handleAddBrick(brickType.type)}>
                  <ListItemIcon>
                     <brickType.icon fontSize="small"/>
                  </ListItemIcon>
                  <ListItemText>{brickType.label}</ListItemText>
               </MenuItem>
            ))}
         </Menu>

         <Menu
            anchorEl={pasteMenuAnchorEl}
            open={Boolean(pasteMenuAnchorEl)}
            onClose={() => setPasteMenuAnchorEl(null)}
            anchorOrigin={{
               vertical: 'top',
               horizontal: 'left',
            }}
            transformOrigin={{
               vertical: 'top',
               horizontal: 'left',
            }}
            style={{zIndex: 9999}}
         >
            <MenuItem onClick={() => pastePosition !== null && handlePasteBrick(pastePosition)}>
               <ListItemIcon>
                  <ContentPasteIcon fontSize="small"/>
               </ListItemIcon>
               <ListItemText>Paste After This Brick</ListItemText>
            </MenuItem>
         </Menu>

         <form onSubmit={formSubmit} id={"superform"}/>
         <div className={classes.container} onClick={onHeaderClick}>
            <div className={classes.header}>
               <TitleText type={"h3"}>
                  {data?.subject}
               </TitleText>
            </div>
            <div className={classes.from}>
               <div className={classes.item}>
                  <Avatar {...StringHelper.stringAvatar(data?.from?.name || 'create2STAY')}/>
               </div>
               <div className={classes.item}>
                  <BodyText type={"body2"}>From: {data?.from?.name}  &lt;{data?.from?.mail}&gt;</BodyText>
                  <BodyText type={"body2"}>To: consumer@email.here</BodyText>
               </div>
            </div>
         </div>

         <div onClick={onHeaderClick} dangerouslySetInnerHTML={{__html: StartHtml(data)}}/>
         <div id="mail-root"></div>
      </Paper>
   );
}